import { createContext, useContext, useState, useEffect } from 'react'
import api from '../lib/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('easica-admin-token')
      if (!token) {
        setIsLoading(false)
        return
      }

      // Verify token with backend
      const response = await api.get('/admin/auth/me')
      setUser(response.data.user)
      setIsAuthenticated(true)
    } catch (error) {
      console.error('Auth check failed:', error)
      localStorage.removeItem('easica-admin-token')
      localStorage.removeItem('easica-admin-user')
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      const response = await api.post('/admin/auth/login', credentials)
      console.log('Login response:', response.data)
      
      // Backend returns: { success, message, user, token }
      const { user, token } = response.data
      
      if (!token || !user) {
        console.error('Invalid response structure:', response.data)
        return { 
          success: false, 
          error: 'Invalid server response' 
        }
      }
      
      localStorage.setItem('easica-admin-token', token)
      localStorage.setItem('easica-admin-user', JSON.stringify(user))
      
      setUser(user)
      setIsAuthenticated(true)
      
      return { success: true }
    } catch (error) {
      console.error('Login failed:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      }
    }
  }

  const logout = () => {
    localStorage.removeItem('easica-admin-token')
    localStorage.removeItem('easica-admin-user')
    setUser(null)
    setIsAuthenticated(false)
  }

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
